import {
    GetOffersResult,
    getOffersResultSchema,
} from '@west-australian-newspapers/subscription-offer-types'
import { Logger } from '@west-australian-newspapers/typescript-log'
import { getSubscriptionOffersPath } from '../../authentication/helpers'
import { httpGet } from '../../helpers/http'

function validateSubscriptionOfferResponse(value: any): GetOffersResult {
    const toValidate = value

    if (!toValidate) {
        throw new Error('Subscription Offer API data missing')
    }

    const parsedObject = getOffersResultSchema.safeParse(toValidate)
    if (!parsedObject.success) {
        throw new Error(
            `Unable to parse Subscription offer API response: ${JSON.stringify(
                parsedObject.error,
            )}`,
        )
    } else {
        return parsedObject.data
    }
}

/** Resolves the publication the offers should be fetched for, falling back to the SSR hostname */
export const getSubscriptionOfferPublicationId = (publicHostname: string) => {
    const hostname =
        typeof window === 'undefined'
            ? publicHostname
            : window.location.hostname
    const cleanHostname = hostname.replace(/^www\./, '')

    return cleanHostname.startsWith('localhost') ||
        cleanHostname.endsWith('.dev.swmdigital.io')
        ? 'thewest.dev.swmdigital.io'
        : cleanHostname
}

export const fetchSubscriptionOffers = async (
    publicationId: string,
    log: Logger,
    offerGroup = 'breach',
    variant?: string,
    config?: { subscriptionOfferApi?: string },
): Promise<GetOffersResult> => {
    try {
        const query: Record<string, string> = {}
        if (variant) {
            query.variant = variant
        }

        // For SSR: use https://{publicationId}/connect/subscription-offers (proxy handles auth)
        // For client: use relative /connect/subscription-offers (proxy handles auth)
        const isSSR =
            typeof window === 'undefined' && !!config?.subscriptionOfferApi

        let baseUrl = ''
        const path = getSubscriptionOffersPath.replace(/^\//, '') // 'connect/subscription-offers'

        if (isSSR) {
            // SSR: construct baseURL from publicationId to call the proxy
            baseUrl = `https://${publicationId}`
        }

        const remappedPublicationTargetId =
            publicationId.indexOf('thewest-pr') !== -1
                ? 'thewest.dev.swmdigital.io'
                : publicationId.indexOf('kalminer-pr') !== -1
                ? 'kalminer.dev.swmregional.io'
                : publicationId.indexOf('thewest-swg') !== -1
                ? 'thewest.dev.swmdigital.io'
                : publicationId

        const response = await httpGet<GetOffersResult>({
            log,
            validate: validateSubscriptionOfferResponse,
            baseUrl,
            path,
            query: {
                publicationId: remappedPublicationTargetId, // sanitise to use dev
                offerGroup,
                ...query,
            },
            customHeaders: false,
        })

        log.info(
            {
                offerGroup,
                variantCount: response.variants.length,
                variants: Object.keys(response),
            },
            '[Subscription Offer API] Successfully fetched offers:',
        )
        return response
    } catch (error) {
        log.error(
            { error, publicationId, offerGroup, variant },
            '[Subscription Offer API] Failed to fetch subscription offers. Check your SUBSCRIPTION_OFFER_API_KEY',
        )
        throw error
    }
}
